import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { eventsAPI } from '@/api/events'
import { LiveEvent } from '@/types/event'
import EventCard from '@/components/event/EventCard'
import Header from '@/components/common/Header'
import Loading from '@/components/common/Loading'

function Home() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadEvents()
  }, [])

  const loadEvents = async () => {
    try {
      setIsLoading(true)
      const data = await eventsAPI.getEvents()
      setEvents(data)
    } catch (err: any) {
      setError('イベントの読み込みに失敗しました')
      console.error('Failed to load events:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEventClick = (eventId: string) => {
    navigate(`/events/${eventId}`)
  }

  const liveEvents = events.filter(e => e.status === 'live')
  const upcomingEvents = events.filter(e => e.status === 'scheduled')
  const pastEvents = events.filter(e => e.status === 'ended')

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            ようこそ、{user?.full_name || user?.username}さん
          </h1>
          <p className="text-gray-600 mt-1">利用可能なイベント一覧</p>
        </div>

        {isLoading && <Loading />}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="space-y-8">
            {/* Live Events */}
            {liveEvents.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  ライブ配信中
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {liveEvents.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onClick={() => handleEventClick(event.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Upcoming Events */}
            {upcomingEvents.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  開催予定
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {upcomingEvents.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onClick={() => handleEventClick(event.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  終了したイベント
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {pastEvents.map(event => (
                    <EventCard
                      key={event.id}
                      event={event}
                      onClick={() => handleEventClick(event.id)}
                    />
                  ))}
                </div>
              </section>
            )}

            {events.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">現在利用可能なイベントはありません</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default Home